import '../css/tailwind.css';
import 'leaflet/dist/leaflet.css';

import { createApp, h } from 'vue';
import { createInertiaApp, Head, Link, router } from '@inertiajs/vue3';
import { modal } from 'momentum-modal';
import { createPinia } from 'pinia';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { createI18n } from 'vue-i18n';
import { createGtm } from '@gtm-support/vue-gtm';
import VueNumerals from 'vue-numerals';
import route from 'ziggy-js';
import VueGtag from 'vue-gtag';
import { pageview } from 'vue-gtag';
import Toast from 'vue-toastification';
import en from '../lang/en.json';
import nl from '../lang/nl.json';
import { notifications } from './plugins/notifications';
import numeral from 'numeral';
import FloatingVue from 'floating-vue';
import 'floating-vue/dist/style.css';
import * as Sentry from '@sentry/vue';

const appName = window.document.getElementsByTagName('title')[0]?.innerText || 'Tiqqo';
const isProduction = process.env.NODE_ENV === 'production';

// load a locale
numeral.register('locale', 'nl', {
    delimiters: {
        thousands: '.',
        decimal: ','
    },
    abbreviations: {
        thousand: 'k',
        million: 'm',
        billion: 'b',
        trillion: 't'
    },
    ordinal: function (number) {
        return number === 1 ? 'er' : 'ème';
    },
    currency: {
        symbol: '€'
    }
});

// switch between locales
numeral.locale('nl');

const html = document.querySelector('html');

html.classList.remove('dark');
html.classList.add('light');

// A deploy removes the old hashed chunks, so tabs that were opened before it
// fail to lazy-load their next page. Vite 3 has no `vite:preloadError` event
// (that arrived in Vite 5), so catch the failed dynamic import itself and
// reload once to pick up the new build; the timestamp guard prevents a
// reload loop when the new build is broken too.
const staleChunkMessage = /Importing a module script failed|Failed to fetch dynamically imported module|error loading dynamically imported module|Unable to preload CSS/;

const resolvePage = (name: string) =>
    resolvePageComponent(`../js/pages/${name}.vue`, import.meta.glob('../js/pages/**/*.vue')).catch((error: Error) => {
        const lastReloadAt = Number(sessionStorage.getItem('tiqqo:chunk-reload-at') ?? 0);
        if (!staleChunkMessage.test(error.message) || Date.now() - lastReloadAt < 60_000) {
            throw error;
        }
        sessionStorage.setItem('tiqqo:chunk-reload-at', String(Date.now()));
        window.location.reload();
        return new Promise<never>(() => {});
    });

createInertiaApp({
    title: title => title ? `${title} - ${appName}` : appName,
    resolve: name => resolvePage(name) as any,
    setup({ el, App, props, plugin }) {
        // @ts-ignore
        const i18n = createI18n({
            legacy: false,
            locale: props.initialPage.props.locale ? String(props.initialPage.props.locale) : 'en', // user locale by props
            fallbackLocale: 'en',
            messages: {
                en,
                nl
            },
            useScope: 'global'
        });

        const vueApp = createApp({ render: () => h(App, props) });

        // @ts-ignore
        const sentryConfig = props.initialPage.props.sentry as { dsn: string | null; environment: string } | undefined;
        if (import.meta.env.PROD && sentryConfig?.dsn) {
            Sentry.init({
                app: vueApp,
                dsn: sentryConfig.dsn,
                environment: sentryConfig.environment || 'production',
                release: import.meta.env.VITE_SENTRY_RELEASE as string | undefined,
                integrations: [
                    Sentry.browserTracingIntegration(),
                    Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false })
                ],
                tracesSampleRate: 0.1,
                replaysSessionSampleRate: 0.1,
                replaysOnErrorSampleRate: 1.0,
                sendDefaultPii: false,
                // Errors from code we do not own: in-app webviews (Instagram/
                // Facebook on iOS and Android), browser extensions, the Outlook
                // link scanner and flaky networks. Together they were ~85% of
                // the events in the javascript-vue project without a single one
                // being actionable.
                ignoreErrors: [
                    /window\.webkit\.messageHandlers/,
                    // Chrome op iOS injecteert eigen geminificeerde scripts in
                    // elke pagina; die gooien errors met 1-2 letter namen
                    // ("fa", "ga", "Ba") op een regel die niet in ons document
                    // bestaat. De letters wisselen per Chrome-build.
                    /^[a-zA-Z]{1,2}$/,
                    'Java object is gone',
                    /Object Not Found Matching Id/,
                    /Invalid call to runtime\.sendMessage/,
                    'Could not load "util".', // Google Maps loader failing on the client
                    'Network Error',
                    'Request aborted'
                ],
                denyUrls: [
                    /^chrome-extension:\/\//,
                    /^moz-extension:\/\//,
                    /^safari-(web-)?extension:\/\//,
                    /webkit-masked-url/
                ]
            });

            // @ts-ignore
            const sharedUser = props.initialPage.props.user as { id?: number; current_organisation_id?: number } | false | undefined;
            if (sharedUser && typeof sharedUser === 'object' && sharedUser.id) {
                Sentry.setUser({
                    id: String(sharedUser.id),
                    organisation_id: sharedUser.current_organisation_id ?? null
                });
            }
        }

        const vueTagConfig = {
            config: { id: 'G-LDJCNRWM12' }
        };

        const eventProps = props.initialPage.props.event as { ga_id?: string | null; gtm_id?: string | null } | undefined;

        // Add extra GA id if there is any:
        if (props.initialPage.component.includes('PublicEvent2') && eventProps?.ga_id != null) {
            // @ts-ignore
            vueTagConfig.includes = [
                {
                    id: eventProps?.ga_id
                }
            ];
        }

        if (props.initialPage.component.includes('PublicEvent2') && eventProps?.gtm_id != null) {
            const GtmConfig = createGtm({
                id: eventProps?.gtm_id, // Your GTM single container ID, array of container ids ['GTM-xxxxxx', 'GTM-yyyyyy'] or array of objects [{id: 'GTM-xxxxxx', queryParams: { gtm_auth: 'abc123', gtm_preview: 'env-4', gtm_cookies_win: 'x'}}, {id: 'GTM-yyyyyy', queryParams: {gtm_auth: 'abc234', gtm_preview: 'env-5', gtm_cookies_win: 'x'}}], // Your GTM single container ID or array of container ids ['GTM-xxxxxx', 'GTM-yyyyyy']
                //source: 'https://customurl.com/gtm.js', // Add your own serverside GTM script
                defer: false, // Script can be set to `defer` to speed up page load at the cost of less accurate results (in case visitor leaves before script is loaded, which is unlikely but possible). Defaults to false, so the script is loaded `async` by default
                compatibility: false, // Will add `async` and `defer` to the script tag to not block requests for old browsers that do not support `async`
                enabled: true, // defaults to true. Plugin can be disabled by setting this to false for Ex: enabled: !!GDPR_Cookie (optional)
                debug: true, // Whether or not display console logs debugs (optional)
                loadScript: true, // Whether or not to load the GTM Script (Helpful if you are including GTM manually, but need the dataLayer functionality in your components) (optional)
                trackOnNextTick: false // Whether or not call trackView in Vue.nextTick
            });
            vueApp.use(GtmConfig);
        }

        vueApp
            .use(plugin)
            .use(i18n)
            .use(createPinia())
            .use(VueNumerals)
            .use(FloatingVue)
            .use(modal, {
                resolve: name => resolvePage(name)
            })
            .use(VueGtag, vueTagConfig)
            .use(Toast)
            .use(notifications)
            .mixin({ methods: { route } })
            .component('InertiaHead', Head)
            .component('Link', Link)
            .mount(el);
    }
});

router.on('navigate', event => {
    pageview({ page_path: event.detail.page.url });
    if (import.meta.env.PROD) {
        Sentry.addBreadcrumb({
            category: 'navigation',
            message: event.detail.page.url,
            level: 'info'
        });
    }
});
